import React, { useState, useEffect, useRef, useCallback } from "react";
import env from "@beam-australia/react-env";
import { Accordion, AccordionTab } from "primereact/accordion";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";

import { useTranslation } from "react-i18next";

import { useDebounce } from "../../../../hooks/useDebounce";
import useApi from "../../../../hooks/useApi";

import useSkeleton from "../../../../hooks/useSkeleton";

import helperFormats from "../../../../utils/helperFormats";

import WithholdingsLinesUpdateAmount from "./withholdingsLinesUpdateAmount";
import WithholdingsLinesResolveBilling from "./withholdingsLinesResolveBilling";

const WithholdingsLines = ({ settings }) => {
  const [linesLoading, setLinesLoading] = useState(false);
  // La última lectura de líneas falló: hay que decirlo, no mostrar la grilla vacía.
  const [linesError, setLinesError] = useState(false);

  const s = useSkeleton("commons");
  const api = useApi();
  const { t } = useTranslation();

  const [entities, setEntities] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  const [first, setFirst] = useState(0);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [globalFilter, setGlobalFilter] = useState("");
  const [showOnlyProblematic, setShowOnlyProblematic] = useState(false);

  const [totalRecords, setTotalRecords] = useState(0);

  const [
    updateWithholdingLineAmountDialog,
    setUpdateWithholdingLineAmountDialog,
  ] = useState(false);
  const [updateWithholdingLineId, setUpdateWithholdingLineId] = useState(-1);
  const [updateWithholdingLineAmount, setUpdateWithholdingLineAmount] =
    useState(0);

  // Línea que se está facturando sola desde la grilla, para mostrarle el spinner sólo a ella.
  const [billingLineId, setBillingLineId] = useState(null);

  const [resolveBillingDialog, setResolveBillingDialog] = useState(false);
  const [resolveWithholdingLineId, setResolveWithholdingLineId] = useState(-1);
  const [
    resolveWithholdingLineBeneficiary,
    setResolveWithholdingLineBeneficiary,
  ] = useState("");

  const searchInput = useRef(null);

  // Inicializamos el debounce para la búsqueda
  const debouncedSearchTerm = useDebounce(globalFilter, 2000);
  const dt = useRef(null);
  const billedDt = useRef(null);

  const fetchData = useCallback(async () => {
    setLinesLoading(true);
    try {
      const data = await api.get(
        `${env("API_URL")}/withholdings/withholdingsLines/${settings.entityId}`
      );

      setTotalRecords(data.data.length);
      setEntities(data.data);
      setLinesError(false);
    } catch (error) {
      console.error(t("forms.messages.operation.error"), error);

      // No se pisan las líneas que ya estaban: una grilla en cero es indistinguible de "no hay
      // líneas", y acá lo que pasó es que no se pudieron leer.
      setLinesError(true);
    } finally {
      setLinesLoading(false);
    }
    // linesRefreshToken lo incrementa el formulario después de confirmar cada línea, para que
    // la fila se mueva de la grilla de pendientes a la de facturadas apenas termina.
  }, [settings.entityId, settings.linesRefreshToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const hideDialog = (action) => {
    if (action !== "CANCEL") {
      fetchData();
    }

    setUpdateWithholdingLineAmountDialog(false);
    setResolveBillingDialog(false);
    settings.fetchData();
  };

  const toAmount = (value) => Number(value ?? 0);

  const amountsMatch = (left, right) => {
    return Math.abs(toAmount(left) - toAmount(right)) < 0.01;
  };

  const hasWithholdingLineAmountsMismatch = (rowData) => {
    return !(
      amountsMatch(rowData.amountSent, rowData.amountConfirmed) &&
      amountsMatch(rowData.amountConfirmed, rowData.amountConcilied)
    );
  };

  const visibleEntities = (entities ?? []).filter((rowData) => {
    if (!showOnlyProblematic) {
      return true;
    }

    return hasWithholdingLineAmountsMismatch(rowData);
  });

  // Una línea está terminada cuando su pago quedó firme Y su facturación cerró.
  const isLineBilled = (rowData) => !!rowData.confirmedDate && !!rowData.billedDate;

  const confirmationStatus = (rowData) => {
    if (!rowData.confirmedDate) {
      return {
        label: t("entities.withholdings.withholdingsLines.status.notProcessed"),
        icon: "pi pi-circle",
        color: "#94a3b8",
      };
    }

    // DISABLED se chequea antes que isLineBilled: la línea tiene billedDate para no trabar el
    // proceso, pero no se emitió comprobante y no puede mostrarse como facturada.
    if (rowData.billingStatus === "DISABLED") {
      return {
        label: t("entities.withholdings.withholdingsLines.status.disabled"),
        icon: "pi pi-ban",
        color: "#a16207",
      };
    }

    if (isLineBilled(rowData)) {
      return {
        label: t("entities.withholdings.withholdingsLines.status.billed"),
        icon: "pi pi-check-circle",
        color: "#16a34a",
      };
    }

    switch (rowData.billingStatus) {
      case "TO_VERIFY":
        return {
          label: t("entities.withholdings.withholdingsLines.status.toVerify"),
          icon: "pi pi-exclamation-triangle",
          color: "#f59e0b",
        };
      case "FAILED":
        return {
          label: t("entities.withholdings.withholdingsLines.status.billingFailed"),
          icon: "pi pi-times-circle",
          color: "#dc2626",
        };
      default:
        // El pago ya está firme: el reintento solo va a facturar, nunca a pagar de nuevo.
        return {
          label: t("entities.withholdings.withholdingsLines.status.paidNotBilled"),
          icon: "pi pi-clock",
          color: "#2563eb",
        };
    }
  };

  const confirmationStatusBodyTemplate = (rowData) => {
    const status = confirmationStatus(rowData);

    return (
      <div className="flex align-items-center" style={{ gap: "6px" }}>
        <i className={status.icon} style={{ color: status.color }} />
        <span style={{ color: status.color }}>{status.label}</span>
        {rowData.billingReference ? (
          <span style={{ color: "#64748b" }}>({rowData.billingReference})</span>
        ) : null}
        {rowData.billingStatus === "TO_VERIFY" ? (
          // Única salida de TO_VERIFY: la decide una persona después de mirar el facturador.
          <Button
            label={t(
              "entities.withholdings.withholdingsLines.resolveBilling.action"
            )}
            icon="pi pi-search"
            className="p-button-sm p-button-text"
            onClick={() => {
              setResolveWithholdingLineId(rowData.id);
              setResolveWithholdingLineBeneficiary(
                `${rowData.beneficiary_Name ?? ""} ${
                  rowData.beneficiary_LastName ?? ""
                }`.trim()
              );
              setResolveBillingDialog(true);
            }}
          />
        ) : null}
        {canBillLine(rowData) ? (
          // Reintento de una sola línea, para no volver a recorrer la retención entera por una que
          // falló. Sale con la fecha fijada en la retención, no con la de hoy.
          <Button
            label={t("entities.withholdings.withholdingsLines.billLine")}
            icon="pi pi-file"
            className="p-button-sm p-button-text"
            loading={billingLineId === rowData.id}
            disabled={billingLineId != null}
            onClick={() => billLine(rowData.id)}
          />
        ) : null}
      </div>
    );
  };

  // Una línea se puede facturar sola si ya está cobrada, no tiene comprobante y no está esperando
  // verificación manual (ésa no se refactura sola: puede haber salido un comprobante).
  // Hace falta que la retención tenga fecha de facturación, que es lo que fija la corrida masiva.
  const canBillLine = (rowData) =>
    !!settings.withholdingBillingDate &&
    !!rowData.confirmedDate &&
    !rowData.billedDate &&
    rowData.billingStatus !== "TO_VERIFY";

  const billLine = async (lineId) => {
    setBillingLineId(lineId);

    try {
      await api.put(`${env("API_URL")}/withholdings/confirmLine/${lineId}/billing`);

      settings.toast.current.show({
        severity: "success",
        summary: t("forms.messages.titles.success"),
        detail: t("forms.messages.operation.success"),
        life: 3000,
      });
    } catch (error) {
      settings.toast.current.show({
        severity: "error",
        summary: t("forms.messages.titles.error"),
        detail:
          error?.response?.data?.failure?.reason ??
          t("forms.messages.operation.error"),
        life: 8000,
      });
    } finally {
      setBillingLineId(null);

      // Pase lo que pase la fila cambió de estado, así que se releen las líneas y la cabecera
      // (que es de donde sale si todavía queda algo por facturar).
      await fetchData();
      await settings.fetchData();
    }
  };

  const mismatchAlertBodyTemplate = (rowData) => {
    if (!hasWithholdingLineAmountsMismatch(rowData)) {
      return null;
    }

    return (
      <i
        className="pi pi-exclamation-triangle"
        title="Los montos enviado/confirmado/conciliado no coinciden"
        style={{ color: "#f59e0b", fontSize: "1rem" }}
      />
    );
  };

  const roundAmount = (value) => Number(value ?? 0);

  const getPaymentCutoffDate = () => {
    const createdDate = settings.withholdingCreatedDate
      ? new Date(settings.withholdingCreatedDate)
      : null;

    return createdDate && !Number.isNaN(createdDate.getTime()) ? createdDate : null;
  };

  const isCreditActive = (credit, referenceDate = null) => {
    if (!credit) {
      return false;
    }

    const closedDate = credit.closedDate ? new Date(credit.closedDate) : null;

    if (!closedDate || Number.isNaN(closedDate.getTime()) || closedDate.getFullYear() <= 1) {
      return true;
    }

    if (!referenceDate) {
      return false;
    }

    return closedDate > referenceDate;
  };

  const getActiveCreditsCount = (rowData) => {
    const installments = rowData.installmentsCollection ?? [];
    const referenceDate = getPaymentCutoffDate();
    const activeCredits = new Set();

    installments.forEach((installment) => {
      const credit = installment.credit;
      const creditId = Number(credit?.id ?? installment.creditId ?? 0);

      if (!creditId || !isCreditActive(credit, referenceDate)) {
        return;
      }

      activeCredits.add(creditId);
    });

    return activeCredits.size;
  };

  const getCurrentAccountApplied = (rowData) => {
    return roundAmount(rowData?.currentAccountApplied);
  };

  const getInstallmentComparableDate = (installment) => {
    return new Date(
      installment.expirationDate ?? installment.installmentDate ?? 0
    ).getTime();
  };

  const sortInstallmentsByDate = (installments) => {
    return installments
      .slice()
      .sort((left, right) => getInstallmentComparableDate(left) - getInstallmentComparableDate(right));
  };

  const buildCurrentAccountAllocationMap = (installments, rowData, cutoffDate) => {
    let remainingCurrentAccount = getCurrentAccountApplied(rowData);
    const allocationMap = {};

    sortInstallmentsByDate(installments).forEach((installment) => {
      const pendingTotals = getInstallmentPendingTotals(installment, cutoffDate);
      const installmentBaseTotal =
        roundAmount(pendingTotals.capital) +
        roundAmount(pendingTotals.interests) +
        roundAmount(pendingTotals.interestsTaxes);

      if (remainingCurrentAccount <= 0 || installmentBaseTotal <= 0) {
        allocationMap[installment.id] = 0;
        return;
      }

      const appliedAmount = Math.min(remainingCurrentAccount, installmentBaseTotal);
      allocationMap[installment.id] = appliedAmount;
      remainingCurrentAccount -= appliedAmount;
    });

    return allocationMap;
  };

  const getInstallmentPendingTotals = (installment, cutoffDate = null) => {
    const allPayments = installment.installmentsPaymentsCollection ?? [];
    const payments = cutoffDate
      ? allPayments.filter((payment) => {
        const paymentDate = new Date(payment.date);
        return !Number.isNaN(paymentDate.getTime()) && paymentDate <= cutoffDate;
      })
      : allPayments;

    return {
      capital: Math.max(
        0,
        roundAmount(installment.capital) -
        payments.reduce((total, payment) => total + roundAmount(payment.capital), 0)
      ),
      interests: Math.max(
        0,
        roundAmount(installment.interests) -
        payments.reduce(
          (total, payment) => total + roundAmount(payment.interests),
          0
        )
      ),
      interestsTaxes: Math.max(
        0,
        roundAmount(installment.interestsTaxes) -
        payments.reduce(
          (total, payment) => total + roundAmount(payment.interestsTaxes),
          0
        )
      ),
    };
  };

  const getInstallmentArrears = (installment, referenceDate, paymentCutoffDate) => {
    const allArrears = installment.installmentsArrearsCollection ?? [];
    const arrears = referenceDate
      ? allArrears.filter((arrear) => {
        const d = new Date(arrear.date);
        return !isNaN(d.getTime()) && d <= referenceDate;
      })
      : allArrears;

    return arrears.map((arrear) => {
      const allArrearPayments = arrear.installmentsArrearsPaymentsCollection ?? [];
      const arrearPayments = paymentCutoffDate
        ? allArrearPayments.filter((payment) => {
          const paymentDate = new Date(payment.date);
          return !Number.isNaN(paymentDate.getTime()) && paymentDate <= paymentCutoffDate;
        })
        : allArrearPayments;

      return {
        ...arrear,
        pendingCapital: Math.max(0, roundAmount(arrear.capital) - arrearPayments.reduce((t, p) => t + roundAmount(p.capital), 0)),
        pendingInterests: Math.max(0, roundAmount(arrear.interests) - arrearPayments.reduce((t, p) => t + roundAmount(p.interests), 0)),
        pendingInterestsTaxes: Math.max(0, roundAmount(arrear.interestsTaxes) - arrearPayments.reduce((t, p) => t + roundAmount(p.interestsTaxes), 0)),
        pendingArrearTaxes: Math.max(0, roundAmount(arrear.arrearTaxes) - arrearPayments.reduce((t, p) => t + roundAmount(p.arrearTaxes), 0)),
      };
    });
  };

  const buildSectionTotals = (
    items,
    includeArrears = false,
    includeInstallmentBalances = true,
    referenceDate = null,
    paymentCutoffDate = null
  ) => {
    return items.reduce(
      (totals, installment) => {
        if (includeInstallmentBalances) {
          const installmentPendingTotals = getInstallmentPendingTotals(
            installment,
            paymentCutoffDate
          );

          totals.capital += installmentPendingTotals.capital;
          totals.interests += installmentPendingTotals.interests;
          totals.interestsTaxes += installmentPendingTotals.interestsTaxes;
        }

        if (includeArrears) {
          const allArrears = installment.installmentsArrearsCollection ?? [];

          // Solo se incluyen las moras generadas hasta la fecha de la retención
          // (día 10 del mes del período). Las moras de períodos posteriores no
          // pertenecen a esta retención aunque estén en el mismo installment.
          const arrears = referenceDate
            ? allArrears.filter((arrear) => {
              const d = new Date(arrear.date);
              return !isNaN(d.getTime()) && d <= referenceDate;
            })
            : allArrears;

          arrears.forEach((arrear) => {
            const allArrearPayments =
              arrear.installmentsArrearsPaymentsCollection ?? [];
            const arrearPayments = paymentCutoffDate
              ? allArrearPayments.filter((payment) => {
                const paymentDate = new Date(payment.date);
                return !Number.isNaN(paymentDate.getTime()) && paymentDate <= paymentCutoffDate;
              })
              : allArrearPayments;

            totals.capital += Math.max(
              0,
              roundAmount(arrear.capital) -
              arrearPayments.reduce(
                (t, p) => t + roundAmount(p.capital),
                0
              )
            );
            totals.interests += Math.max(
              0,
              roundAmount(arrear.interests) -
              arrearPayments.reduce(
                (t, p) => t + roundAmount(p.interests),
                0
              )
            );
            totals.interestsTaxes += Math.max(
              0,
              roundAmount(arrear.interestsTaxes) -
              arrearPayments.reduce(
                (t, p) => t + roundAmount(p.interestsTaxes),
                0
              )
            );
            totals.arrearTaxes += Math.max(
              0,
              roundAmount(arrear.arrearTaxes) -
              arrearPayments.reduce(
                (t, p) => t + roundAmount(p.arrearTaxes),
                0
              )
            );
          });
        }

        return totals;
      },
      {
        capital: 0,
        interests: 0,
        interestsTaxes: 0,
        arrearTaxes: 0,
      }
    );
  };

  const getSectionTotal = (section) => {
    return (
      roundAmount(section.capital) +
      roundAmount(section.interests) +
      roundAmount(section.interestsTaxes) +
      roundAmount(section.arrearTaxes)
    );
  };

  const getReferencePeriod = () => {
    const year = Number(settings.withholdingYear);
    const month = Number(settings.withholdingMonth);

    if (!year || !month) {
      return null;
    }

    return { year, month };
  };

  const getInstallmentPeriod = (installment) => {
    const rawDate = installment?.expirationDate ?? installment?.installmentDate;

    if (!rawDate) {
      return null;
    }

    const date = new Date(rawDate);

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      label: helperFormats.formatDate(date),
    };
  };

  const getLineBreakdown = (rowData) => {
    const installments = rowData.installmentsCollection ?? [];
    const referencePeriod = getReferencePeriod();
    const paymentCutoffDate = getPaymentCutoffDate();

    const currentInstallments = [];
    const previousInstallments = [];

    installments.forEach((installment) => {
      const period = getInstallmentPeriod(installment);

      if (
        referencePeriod &&
        period &&
        period.year === referencePeriod.year &&
        period.month === referencePeriod.month
      ) {
        currentInstallments.push(installment);
        return;
      }

      previousInstallments.push(installment);
    });

    const withholdingReferenceDate = referencePeriod
      ? new Date(referencePeriod.year, referencePeriod.month - 1, 10)
      : null;

    const arrears = buildSectionTotals(
      installments,
      true,
      false,
      withholdingReferenceDate,
      paymentCutoffDate
    );
    const current = buildSectionTotals(
      currentInstallments,
      false,
      true,
      null,
      paymentCutoffDate
    );
    const previous = buildSectionTotals(
      previousInstallments,
      false,
      true,
      null,
      paymentCutoffDate
    );

    return {
      arrears,
      current,
      previous,
      currentInstallments,
      previousInstallments,
      withholdingReferenceDate,
    };
  };

  const escapeCsvValue = (value) => {
    const normalizedValue = `${value ?? ""}`;
    const escapedValue = normalizedValue.replace(/"/g, '""');
    return `"${escapedValue}"`;
  };

  const getWithholdingPaymentIdSet = (rowData) => {
    const ids = rowData?.withholdingPaymentIds ?? [];
    return new Set(ids.map((id) => Number(id)).filter((id) => !Number.isNaN(id)));
  };

  const filterPaymentsByWithholding = (payments, paymentIdSet) => {
    if (!paymentIdSet || paymentIdSet.size === 0) {
      return [];
    }
    return (payments ?? []).filter((p) =>
      paymentIdSet.has(Number(p.paymentId))
    );
  };

  const getInstallmentConfirmedPayments = (installment, paymentIdSet) => {
    const confirmedPayments = filterPaymentsByWithholding(
      installment.installmentsPaymentsCollection,
      paymentIdSet
    );

    return {
      capital: confirmedPayments.reduce((t, p) => t + roundAmount(p.capital ?? 0), 0),
      interests: confirmedPayments.reduce((t, p) => t + roundAmount(p.interests ?? 0), 0),
      interestsTaxes: confirmedPayments.reduce((t, p) => t + roundAmount(p.interestsTaxes ?? 0), 0),
    };
  };

  const getArrearConfirmedPayments = (arrear, paymentIdSet) => {
    const confirmedPayments = filterPaymentsByWithholding(
      arrear.installmentsArrearsPaymentsCollection,
      paymentIdSet
    );

    return {
      capital: confirmedPayments.reduce((t, p) => t + roundAmount(p.capital ?? 0), 0),
      interests: confirmedPayments.reduce((t, p) => t + roundAmount(p.interests ?? 0), 0),
      interestsTaxes: confirmedPayments.reduce((t, p) => t + roundAmount(p.interestsTaxes ?? 0), 0),
      arrearTaxes: confirmedPayments.reduce((t, p) => t + roundAmount(p.arrearTaxes ?? 0), 0),
    };
  };

  const buildInstallmentExportRows = (rowData) => {
    const amountConfirmed = roundAmount(rowData.amountConfirmed);
    const totalCurrentAccountApplied = getCurrentAccountApplied(rowData);

    // Calcular crédito a cuenta corriente: lo que sobró del pago y fue a CC
    const withholdingPaymentsForCC = rowData.withholdingPayments ?? [];
    const totalPaymentAmount = withholdingPaymentsForCC.reduce((sum, p) => sum + roundAmount(p.amount), 0);
    const totalInstallmentsPaid = withholdingPaymentsForCC.reduce((sum, p) => {
      const ipTotal = (p.installmentsPaymentsCollection ?? []).reduce(
        (s, ip) => s + roundAmount(ip.capital) + roundAmount(ip.interests) + roundAmount(ip.interestsTaxes), 0);
      const iapTotal = (p.installmentsArrearsPaymentsCollection ?? []).reduce(
        (s, iap) => s + roundAmount(iap.capital) + roundAmount(iap.interests) + roundAmount(iap.interestsTaxes) + roundAmount(iap.arrearTaxes), 0);
      return sum + ipTotal + iapTotal;
    }, 0);
    const currentAccountCredit = roundAmount(Math.max(0, totalPaymentAmount - totalInstallmentsPaid));

    // Neto de cuenta corriente: positivo = fue a CC, negativo = se usó de CC
    const currentAccountNet = roundAmount(currentAccountCredit - totalCurrentAccountApplied);

    const baseRow = {
      withholdingsLineId: rowData.id,
      beneficiaryDocumentNumber: rowData.beneficiary_DocumentNumber ?? "",
      beneficiaryName: rowData.beneficiary_Name ?? "",
      beneficiaryLastName: rowData.beneficiary_LastName ?? "",
      creditsActive: getActiveCreditsCount(rowData),
      amountGenerated: roundAmount(rowData.amountGenerated),
      amountSent: roundAmount(rowData.amountSent),
      amountConfirmed,
      amountConcilied: roundAmount(rowData.amountConcilied),
      currentAccountApplied: currentAccountNet,
      hasMismatch: hasWithholdingLineAmountsMismatch(rowData) ? "SI" : "NO",
    };

    // Agrupa por cuota REAL (la que aparece en los pagos persistidos del
    // withholding para este beneficiary), no por la cuota originalmente
    // listada en wl.installmentsCollection. Esto es necesario porque al
    // confirmar la retención los pagos se aplican a las cuotas vigentes en
    // ese momento (orden FIFO), que pueden diferir de las originalmente
    // asociadas a la withholdingsLine.
    const withholdingPayments = rowData.withholdingPayments ?? [];
    const installmentBuckets = new Map();

    const ensureBucket = (installment) => {
      if (!installment) {
        return null;
      }
      const installmentId = Number(installment.id);
      if (!installmentBuckets.has(installmentId)) {
        installmentBuckets.set(installmentId, {
          installment,
          installmentCapital: 0,
          installmentInterests: 0,
          installmentInterestsTaxes: 0,
          arrearsCapital: 0,
          arrearsInterests: 0,
          arrearsInterestsTaxes: 0,
          arrearsTaxes: 0,
          arrearsDays: 0,
          arrearsSeen: new Set(),
        });
      }
      return installmentBuckets.get(installmentId);
    };

    withholdingPayments.forEach((payment) => {
      (payment.installmentsPaymentsCollection ?? []).forEach((ip) => {
        const bucket = ensureBucket(ip.installment);
        if (!bucket) return;
        bucket.installmentCapital += roundAmount(ip.capital);
        bucket.installmentInterests += roundAmount(ip.interests);
        bucket.installmentInterestsTaxes += roundAmount(ip.interestsTaxes);
      });

      (payment.installmentsArrearsPaymentsCollection ?? []).forEach((iap) => {
        const arrear = iap.installmentArrear;
        if (!arrear) return;
        const bucket = ensureBucket(arrear.installment);
        if (!bucket) return;
        bucket.arrearsCapital += roundAmount(iap.capital);
        bucket.arrearsInterests += roundAmount(iap.interests);
        bucket.arrearsInterestsTaxes += roundAmount(iap.interestsTaxes);
        bucket.arrearsTaxes += roundAmount(iap.arrearTaxes);
        // Solo se cuentan los días una vez por arrear.
        const arrearId = Number(arrear.id);
        if (!bucket.arrearsSeen.has(arrearId)) {
          bucket.arrearsSeen.add(arrearId);
          bucket.arrearsDays += Number(arrear.days ?? 0);
        }
      });
    });

    // Si no hay pagos persistidos (retención no confirmada o sin pagos al
    // beneficiary), se devuelve una única fila vacía para preservar trazabilidad.
    if (installmentBuckets.size === 0) {
      return [
        {
          ...baseRow,
          creditCode: "",
          installmentNumber: 0,
          installmentTerm: 0,
          installmentDueDate: "",
          installmentTotal: 0,
          currentAccountInstallmentApplied: 0,
          arrearsDays: 0,
          arrearsCapital: 0,
          arrearsInterests: 0,
          arrearsInterestsTaxes: 0,
          arrearsTaxes: 0,
          arrearsTotal: 0,
          confirmedInstallmentCapital: 0,
          confirmedInstallmentInterests: 0,
          confirmedInstallmentTaxes: 0,
          confirmedArrearCapital: 0,
          confirmedArrearInterests: 0,
          confirmedArrearInterestsTaxes: 0,
          confirmedArrearTaxes: 0,
          confirmedPaymentsTotal: 0,
          confirmedPaymentsDifference: amountConfirmed,
          confirmedPaymentsControl: amountConfirmed === 0 ? "OK" : "NO CUADRA",
        },
      ];
    }

    const buckets = Array.from(installmentBuckets.values()).sort(
      (a, b) => getInstallmentComparableDate(a.installment) - getInstallmentComparableDate(b.installment)
    );

    const totalInstallmentConfirmedBase = buckets.reduce(
      (total, b) =>
        total +
        roundAmount(b.installmentCapital) +
        roundAmount(b.installmentInterests) +
        roundAmount(b.installmentInterestsTaxes),
      0
    );

    return buckets.map((bucket) => {
      const { installment } = bucket;
      const period = getInstallmentPeriod(installment);
      const installmentNumber = Number(installment.number ?? 0);
      const creditInstallmentsNumber = Number(
        installment.credit?.installmentsNumber ?? 0
      );

      const confirmedInstallmentBase = roundAmount(
        bucket.installmentCapital +
        bucket.installmentInterests +
        bucket.installmentInterestsTaxes
      );

      // Distribución proporcional del crédito de cuenta corriente entre las
      // cuotas pagadas por esta retención.
      const currentAccountInstallmentApplied =
        totalInstallmentConfirmedBase > 0
          ? roundAmount(
            (confirmedInstallmentBase / totalInstallmentConfirmedBase) *
            totalCurrentAccountApplied
          )
          : 0;

      const arrearsTotal = roundAmount(
        bucket.arrearsCapital +
        bucket.arrearsInterests +
        bucket.arrearsInterestsTaxes +
        bucket.arrearsTaxes
      );

      const confirmedPaymentsTotal = roundAmount(
        confirmedInstallmentBase + arrearsTotal
      );
      const confirmedPaymentsDifference = roundAmount(
        amountConfirmed - confirmedPaymentsTotal
      );

      return {
        ...baseRow,
        creditCode: installment.credit?.code ?? "",
        installmentNumber,
        installmentTerm: creditInstallmentsNumber,
        installmentDueDate: period?.label ?? "",
        installmentTotal: confirmedInstallmentBase,
        currentAccountInstallmentApplied,
        arrearsDays: bucket.arrearsDays,
        arrearsCapital: roundAmount(bucket.arrearsCapital),
        arrearsInterests: roundAmount(bucket.arrearsInterests),
        arrearsInterestsTaxes: roundAmount(bucket.arrearsInterestsTaxes),
        arrearsTaxes: roundAmount(bucket.arrearsTaxes),
        arrearsTotal,
        confirmedInstallmentCapital: roundAmount(bucket.installmentCapital),
        confirmedInstallmentInterests: roundAmount(bucket.installmentInterests),
        confirmedInstallmentTaxes: roundAmount(bucket.installmentInterestsTaxes),
        confirmedArrearCapital: roundAmount(bucket.arrearsCapital),
        confirmedArrearInterests: roundAmount(bucket.arrearsInterests),
        confirmedArrearInterestsTaxes: roundAmount(bucket.arrearsInterestsTaxes),
        confirmedArrearTaxes: roundAmount(bucket.arrearsTaxes),
        confirmedPaymentsTotal,
        confirmedPaymentsDifference,
        confirmedPaymentsControl:
          Math.abs(confirmedPaymentsDifference) < 0.01 ? "OK" : "NO CUADRA",
      };
    });
  };

  const exportSimpleCSV = () => {
    const simpleHeaders = [
      "Documento",
      "Nombre",
      "Apellido",
      "Monto Enviado",
      "Monto Confirmado",
      "Monto Conciliado",
    ];

    const simpleRows = visibleEntities.map((rowData) => [
      rowData.beneficiary_DocumentNumber ?? "",
      rowData.beneficiary_Name ?? "",
      rowData.beneficiary_LastName ?? "",
      roundAmount(rowData.amountSent),
      roundAmount(rowData.amountConfirmed),
      roundAmount(rowData.amountConcilied),
    ]);

    const csvLines = [
      simpleHeaders.map(escapeCsvValue).join(","),
      ...simpleRows.map((row) => row.map(escapeCsvValue).join(",")),
    ];

    const csvContent = `\uFEFF${csvLines.join("\n")}`;
    const csvBlob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const downloadUrl = URL.createObjectURL(csvBlob);
    const downloadAnchor = document.createElement("a");
    downloadAnchor.href = downloadUrl;
    downloadAnchor.download = `withholdings-lines-${settings.entityId}.csv`;
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    document.body.removeChild(downloadAnchor);
    URL.revokeObjectURL(downloadUrl);
  };

  const exportCSV = () => {
    const exportRows = visibleEntities.flatMap((rowData) =>
      buildInstallmentExportRows(rowData)
    );

    // Los importes provienen de los pagos persistidos en BD (filtrados por
    // los Payments de esta retención), por lo que no se aplica normalización
    // proporcional. La igualdad confirmedPaymentsTotal == amountConfirmed
    // queda como control de auditoría en la columna correspondiente.
    const normalizedExportRows = exportRows;

    const groupedRowsByLineId = normalizedExportRows.reduce((acc, row) => {
      const key = row.withholdingsLineId;

      if (!acc[key]) {
        acc[key] = [];
      }

      acc[key].push(row);
      return acc;
    }, {});

    const summaryRows = Object.values(groupedRowsByLineId).map((rows) => {
      const firstRow = rows[0] ?? {};

      const totals = rows.reduce(
        (acc, row) => {
          acc.installmentTotal += roundAmount(row.installmentTotal);
          acc.currentAccountInstallmentApplied += roundAmount(
            row.currentAccountInstallmentApplied
          );
          acc.arrearsDays += Number(row.arrearsDays ?? 0);
          acc.arrearsCapital += roundAmount(row.arrearsCapital);
          acc.arrearsInterests += roundAmount(row.arrearsInterests);
          acc.arrearsInterestsTaxes += roundAmount(row.arrearsInterestsTaxes);
          acc.arrearsTaxes += roundAmount(row.arrearsTaxes);
          acc.arrearsTotal += roundAmount(row.arrearsTotal);
          acc.confirmedInstallmentCapital += roundAmount(
            row.confirmedInstallmentCapital
          );
          acc.confirmedInstallmentInterests += roundAmount(
            row.confirmedInstallmentInterests
          );
          acc.confirmedInstallmentTaxes += roundAmount(
            row.confirmedInstallmentTaxes
          );
          acc.confirmedArrearCapital += roundAmount(row.confirmedArrearCapital);
          acc.confirmedArrearInterests += roundAmount(
            row.confirmedArrearInterests
          );
          acc.confirmedArrearInterestsTaxes += roundAmount(
            row.confirmedArrearInterestsTaxes
          );
          acc.confirmedArrearTaxes += roundAmount(row.confirmedArrearTaxes);

          return acc;
        },
        {
          installmentTotal: 0,
          currentAccountInstallmentApplied: 0,
          arrearsDays: 0,
          arrearsCapital: 0,
          arrearsInterests: 0,
          arrearsInterestsTaxes: 0,
          arrearsTaxes: 0,
          arrearsTotal: 0,
          confirmedInstallmentCapital: 0,
          confirmedInstallmentInterests: 0,
          confirmedInstallmentTaxes: 0,
          confirmedArrearCapital: 0,
          confirmedArrearInterests: 0,
          confirmedArrearInterestsTaxes: 0,
          confirmedArrearTaxes: 0,
        }
      );

      const confirmedPaymentsTotal = roundAmount(
        totals.confirmedInstallmentCapital +
        totals.confirmedInstallmentInterests +
        totals.confirmedInstallmentTaxes +
        totals.confirmedArrearCapital +
        totals.confirmedArrearInterests +
        totals.confirmedArrearInterestsTaxes +
        totals.confirmedArrearTaxes
      );
      const amountConfirmed = roundAmount(firstRow.amountConfirmed);
      const confirmedPaymentsDifference = roundAmount(
        amountConfirmed - confirmedPaymentsTotal
      );

      return {
        ...firstRow,
        rowType: "RESUMEN",
        creditCode: "RESUMEN",
        installmentNumber: "",
        installmentTerm: "",
        installmentDueDate: "",
        installmentTotal: roundAmount(totals.installmentTotal),
        currentAccountInstallmentApplied: roundAmount(
          totals.currentAccountInstallmentApplied
        ),
        arrearsDays: totals.arrearsDays,
        arrearsCapital: roundAmount(totals.arrearsCapital),
        arrearsInterests: roundAmount(totals.arrearsInterests),
        arrearsInterestsTaxes: roundAmount(totals.arrearsInterestsTaxes),
        arrearsTaxes: roundAmount(totals.arrearsTaxes),
        arrearsTotal: roundAmount(totals.arrearsTotal),
        confirmedInstallmentCapital: roundAmount(
          totals.confirmedInstallmentCapital
        ),
        confirmedInstallmentInterests: roundAmount(
          totals.confirmedInstallmentInterests
        ),
        confirmedInstallmentTaxes: roundAmount(totals.confirmedInstallmentTaxes),
        confirmedArrearCapital: roundAmount(totals.confirmedArrearCapital),
        confirmedArrearInterests: roundAmount(totals.confirmedArrearInterests),
        confirmedArrearInterestsTaxes: roundAmount(
          totals.confirmedArrearInterestsTaxes
        ),
        confirmedArrearTaxes: roundAmount(totals.confirmedArrearTaxes),
        confirmedPaymentsTotal,
        confirmedPaymentsDifference,
        confirmedPaymentsControl:
          Math.abs(confirmedPaymentsDifference) < 0.01 ? "OK" : "NO CUADRA",
      };
    });

    const exportRowsWithSummary = [
      ...normalizedExportRows.map((row) => ({
        ...row,
        rowType: "DETALLE",
        confirmedPaymentsDifference: "",
        confirmedPaymentsControl: "",
      })),
      ...summaryRows,
    ];

    const withholdingYear = settings.withholdingYear ?? "";
    const withholdingMonth = settings.withholdingMonth ?? "";

    const csvRowValues = (row) => [
      withholdingYear,
      withholdingMonth,
      row.rowType,
      row.withholdingsLineId,
      row.beneficiaryDocumentNumber,
      row.beneficiaryName,
      row.beneficiaryLastName,
      row.creditsActive,
      row.amountGenerated,
      row.amountSent,
      row.amountConfirmed,
      row.amountConcilied,
      row.currentAccountApplied,
      row.creditCode,
      row.installmentNumber,
      row.installmentTotal,
      row.arrearsDays,
      row.arrearsCapital,
      row.arrearsInterests,
      row.arrearsInterestsTaxes,
      row.arrearsTaxes,
      row.arrearsTotal,
      row.confirmedInstallmentCapital,
      row.confirmedInstallmentInterests,
      row.confirmedInstallmentTaxes,
      row.confirmedArrearCapital,
      row.confirmedArrearInterests,
      row.confirmedArrearInterestsTaxes,
      row.confirmedArrearTaxes,
      row.confirmedPaymentsDifference,
      row.confirmedPaymentsControl,
    ];

    const headers = [
      "Año",
      "Mes",
      "Tipo Fila",
      "Withholding Line Id",
      "Documento",
      "Nombre",
      "Apellido",
      "Creditos Activos",
      "Monto Generado",
      "Monto Enviado",
      "Monto Confirmado",
      "Monto Conciliado",
      "Cuenta Corriente",
      "Codigo Credito",
      "Cuota",
      "Total Cuota",
      "Mora Dias",
      "Mora Capital",
      "Mora Intereses",
      "Mora Impuestos Intereses",
      "Mora Impuestos Mora",
      "Mora Total",
      "Pagos Confirmados Cuota Capital",
      "Pagos Confirmados Cuota Intereses",
      "Pagos Confirmados Cuota Impuestos",
      "Pagos Confirmados Mora Capital",
      "Pagos Confirmados Mora Intereses",
      "Pagos Confirmados Mora Impuestos Intereses",
      "Pagos Confirmados Mora Impuestos Mora",
      "Control",
      "Control Estado",
    ];

    const csvLines = [
      headers.map(escapeCsvValue).join(","),
      ...exportRowsWithSummary.map((row) =>
        csvRowValues(row).map(escapeCsvValue).join(",")
      ),
    ];

    const csvContent = `\uFEFF${csvLines.join("\n")}`;
    const csvBlob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const downloadUrl = URL.createObjectURL(csvBlob);
    const downloadAnchor = document.createElement("a");
    downloadAnchor.href = downloadUrl;
    downloadAnchor.download = `withholdings-lines-${settings.entityId}.csv`;
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    document.body.removeChild(downloadAnchor);
    URL.revokeObjectURL(downloadUrl);
  };

  const renderSectionValue = (label, value) => {
    return (
      <div className="withholdings-lines-breakdown-row" key={label}>
        <span>{label}</span>
        <strong>{helperFormats.formatDecimal(value)}</strong>
      </div>
    );
  };

  const renderInstallmentsList = (installments, emptyLabel, showArrears = false, referenceDate = null, currentAccountAllocationMap = {}) => {
    const paymentCutoffDate = getPaymentCutoffDate();

    if (!installments.length) {
      return (
        <div className="withholdings-lines-breakdown-empty">{emptyLabel}</div>
      );
    }

    return (
      <div className="withholdings-lines-breakdown-list">
        {sortInstallmentsByDate(installments)
          .map((installment) => {
            const period = getInstallmentPeriod(installment);
            const installmentPendingTotals = getInstallmentPendingTotals(
              installment,
              paymentCutoffDate
            );
            const installmentBaseTotal =
              installmentPendingTotals.capital +
              installmentPendingTotals.interests +
              installmentPendingTotals.interestsTaxes;
            const currentAccountInstallmentApplied = roundAmount(
              currentAccountAllocationMap[installment.id]
            );
            const installmentTotal = Math.max(
              0,
              installmentBaseTotal - currentAccountInstallmentApplied
            );
            const creditTotalInstallments = Number(
              installment.credit?.installmentsNumber ?? 0
            );

            const installmentArrears = showArrears
              ? getInstallmentArrears(installment, referenceDate, paymentCutoffDate)
              : [];
            const arrearBreakdown = installmentArrears.reduce(
              (totals, arrear) => {
                totals.capital += roundAmount(arrear.pendingCapital);
                totals.interests += roundAmount(arrear.pendingInterests);
                totals.interestsTaxes += roundAmount(arrear.pendingInterestsTaxes);
                totals.arrearTaxes += roundAmount(arrear.pendingArrearTaxes);

                return totals;
              },
              {
                capital: 0,
                interests: 0,
                interestsTaxes: 0,
                arrearTaxes: 0,
              }
            );
            const arrearTotal = installmentArrears.reduce(
              (sum, a) => sum + a.pendingCapital + a.pendingInterests + a.pendingInterestsTaxes + a.pendingArrearTaxes,
              0
            );
            const arrearDaysTotal = installmentArrears.reduce(
              (sum, a) => sum + Number(a.days ?? 0),
              0
            );

            return (
              <div
                className="withholdings-lines-breakdown-installment-block"
                key={installment.id}
              >
                <div className="withholdings-lines-breakdown-list-item">
                  <span>
                    {installment.credit?.code ? `${installment.credit.code} | ` : ""}
                    Cuota {installment.number}
                    {creditTotalInstallments > 0
                      ? ` de ${creditTotalInstallments}`
                      : ""}
                    {period ? ` | ${period.label}` : ""}
                  </span>
                  <strong>{helperFormats.formatDecimal(installmentTotal)}</strong>
                </div>
                {currentAccountInstallmentApplied > 0 && (
                  <div className="withholdings-lines-breakdown-current-account-applied">
                    Cuenta corriente aplicada: -{helperFormats.formatDecimal(currentAccountInstallmentApplied)}
                  </div>
                )}
                {installmentArrears.length > 0 && (
                  <div className="withholdings-lines-breakdown-arrears">
                    <div className="withholdings-lines-breakdown-arrear-row withholdings-lines-breakdown-arrear-header">
                      <span>Días</span>
                      <span>Capital</span>
                      <span>Intereses</span>
                      <span>Imp. intereses</span>
                      <span>Imp. mora</span>
                      <span>Total mora</span>
                    </div>
                    <div className="withholdings-lines-breakdown-arrear-row">
                      <span>{arrearDaysTotal} {arrearDaysTotal === 1 ? "día" : "días"}</span>
                      <span>{helperFormats.formatDecimal(arrearBreakdown.capital)}</span>
                      <span>{helperFormats.formatDecimal(arrearBreakdown.interests)}</span>
                      <span>{helperFormats.formatDecimal(arrearBreakdown.interestsTaxes)}</span>
                      <span>{helperFormats.formatDecimal(arrearBreakdown.arrearTaxes)}</span>
                      <strong>{helperFormats.formatDecimal(arrearTotal)}</strong>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
      </div>
    );
  };

  const rowExpansionTemplate = (rowData) => {
    const breakdown = getLineBreakdown(rowData);
    const activeCreditsCount = getActiveCreditsCount(rowData);
    const paymentCutoffDate = getPaymentCutoffDate();
    const currentAccountAllocationMap = buildCurrentAccountAllocationMap(
      rowData.installmentsCollection ?? [],
      rowData,
      paymentCutoffDate
    );

    return (
      <div className="withholdings-lines-breakdown">
        <div className="withholdings-lines-breakdown-summary">
          <div className="withholdings-lines-breakdown-summary-card">
            <span>Total generado</span>
            <strong>
              {helperFormats.formatDecimal(rowData.amountGenerated ?? 0)}
            </strong>
          </div>
          <div className="withholdings-lines-breakdown-summary-card">
            <span>Mora</span>
            <strong>
              {helperFormats.formatDecimal(getSectionTotal(breakdown.arrears))}
            </strong>
          </div>
          <div className="withholdings-lines-breakdown-summary-card">
            <span>Cuotas anteriores</span>
            <strong>
              {helperFormats.formatDecimal(getSectionTotal(breakdown.previous))}
            </strong>
          </div>
          <div className="withholdings-lines-breakdown-summary-card">
            <span>Cuota actual</span>
            <strong>
              {helperFormats.formatDecimal(getSectionTotal(breakdown.current))}
            </strong>
          </div>
          <div className="withholdings-lines-breakdown-summary-card">
            <span>Créditos activos</span>
            <strong>{activeCreditsCount}</strong>
          </div>
        </div>

        <Accordion multiple className="withholdings-lines-breakdown-accordion">
          <AccordionTab
            header={`Cuotas anteriores (${helperFormats.formatDecimal(
              getSectionTotal(breakdown.previous)
            )})`}
          >
            <div className="withholdings-lines-breakdown-grid">
              {renderSectionValue("Capital", breakdown.previous.capital)}
              {renderSectionValue("Intereses", breakdown.previous.interests)}
              {renderSectionValue(
                "Impuestos sobre intereses",
                breakdown.previous.interestsTaxes
              )}
            </div>
            {renderInstallmentsList(
              breakdown.previousInstallments,
              "No hay cuotas anteriores incluidas en esta retención.",
              true,
              breakdown.withholdingReferenceDate,
              currentAccountAllocationMap
            )}
          </AccordionTab>
          <AccordionTab
            header={`Cuota actual (${helperFormats.formatDecimal(
              getSectionTotal(breakdown.current)
            )})`}
          >
            <div className="withholdings-lines-breakdown-grid">
              {renderSectionValue("Capital", breakdown.current.capital)}
              {renderSectionValue("Intereses", breakdown.current.interests)}
              {renderSectionValue(
                "Impuestos sobre intereses",
                breakdown.current.interestsTaxes
              )}
            </div>
            {renderInstallmentsList(
              breakdown.currentInstallments,
              "No hay cuotas del período actual incluidas en esta retención.",
              true,
              breakdown.withholdingReferenceDate,
              currentAccountAllocationMap
            )}
          </AccordionTab>
        </Accordion>
      </div>
    );
  };

  const header = (
    <div className="table-header">
      <div className="flex w-12">
        <div className="flex justify-content-start">
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              ref={searchInput}
              type="search"
              onInput={(e) => setGlobalFilter(e.target.value)}
              placeholder={`${t("DataTable.Search")}...`}
            />
          </span>
          <Button
            outlined
            label={t("DataTable.Export")}
            icon="pi pi-upload"
            className="p-button-secondary"
            onClick={exportSimpleCSV}
            style={{
              marginLeft: "20px",
            }}
          />
          <Button
            outlined
            label="Exportar ampliado"
            icon="pi pi-upload"
            className="p-button-secondary"
            onClick={exportCSV}
            style={{
              marginLeft: "10px",
            }}
          />
          <div
            className="flex align-items-center"
            style={{ marginLeft: "20px", gap: "8px" }}
          >
            <Checkbox
              inputId="withholdings-lines-problematic-only"
              checked={showOnlyProblematic}
              onChange={(e) => setShowOnlyProblematic(!!e.checked)}
            />
            <label htmlFor="withholdings-lines-problematic-only">
              Filtrar inconsistencias
            </label>
          </div>
        </div>
        <div className="flex flex-grow-1 justify-content-end">
          <span className="flex p-2 ">
            {t(
              "entities.withholdings.withholdingsLines.totalWithholdingsLines"
            )}
            : {linesError && entities == null ? "—" : totalRecords}
          </span>
        </div>
      </div>
    </div>
  );

  const withholdingLineAmount = (rowData) => {
    let amount;

    switch (settings.currentWithholdingState) {
      case "WITHHOLDING_STATE_CREATED":
        amount = rowData.amountSent;
        break;
      case "WITHHOLDING_STATE_SENT":
        amount = rowData.amountConfirmed;
        break;
      case "WITHHOLDING_STATE_CONFIRMED":
        amount = rowData.amountConcilied;
        break;
    }

    // El monto confirmado de una línea ya procesada no se puede editar: el pago y el asiento
    // se registraron por ese importe.
    if (
      settings.currentWithholdingState === "WITHHOLDING_STATE_SENT" &&
      rowData.confirmedDate
    ) {
      return (
        <div className="flex justify-content-end">
          {helperFormats.formatDecimal(amount)}
        </div>
      );
    }

    return (
      <div className="flex justify-content-end">
        <Button
          label={`${helperFormats.formatDecimal(amount) ?? "PENDIENTE"}`}
          link
          className="p-0"
          onClick={() => {
            setUpdateWithholdingLineId(rowData.id);
            setUpdateWithholdingLineAmount(amount);
            setUpdateWithholdingLineAmountDialog(true);
          }}
        />
      </div>
    );
  };

  const updateWithholdingLine = (withholdingLineState, id, amount) => {
    switch (settings.currentWithholdingState) {
      case "WITHHOLDING_STATE_CREATED":
        console.log("updateWithholdingLineAmountSent");
        break;
      case "WITHHOLDING_STATE_SENT":
        console.log("updateWithholdingLineAmountConfirmed");
        break;
      case "WITHHOLDING_STATE_CONFIRMED":
        console.log("updateWithholdingLineAmountConcilied");
        break;
    }
  };

  // Una línea cobrada a la que le falta el comprobante: la retención puede estar confirmada (la
  // cabecera se cierra con la plata firme) y estas líneas seguir esperando facturación.
  const hasLinesPendingBilling = visibleEntities.some(
    (rowData) => !!rowData.confirmedDate && !rowData.billedDate
  );

  // Mientras la retención está enviada (o sea, confirmable) las líneas se muestran en dos grillas:
  // las que faltan procesar y las que ya quedaron listas. La segunda hace de barra de progreso y
  // deja a la vista, sin ambigüedad, qué es lo que va a tomar un reintento.
  //
  // Con la retención ya confirmada se siguen mostrando si falta facturar. Ahí está la columna de
  // estado, que es lo único que dice qué línea quedó sin comprobante y lo único que ofrece
  // resolver las que esperan verificación manual o facturar una suelta.
  const showConfirmationGrids =
    settings.currentWithholdingState === "WITHHOLDING_STATE_SENT" ||
    hasLinesPendingBilling;

  // La partición sigue al proceso que está en curso: con la retención sin confirmar lo que se
  // vigila es el cobro, y una vez confirmada, la facturación. Si se partiera siempre por
  // facturación, durante el cobro ninguna línea se movería de grilla y el progreso no se vería.
  const trackingBilling =
    settings.currentWithholdingState !== "WITHHOLDING_STATE_SENT";

  const isLineDone = (rowData) =>
    trackingBilling ? isLineBilled(rowData) : !!rowData.confirmedDate;

  const pendingLines = visibleEntities.filter((rowData) => !isLineDone(rowData));
  const doneLines = visibleEntities.filter(isLineDone);

  // Si no se pudieron leer las líneas no se muestra ningún conteo: un "0" acá se lee como un dato.
  const sinDatos = linesError && entities == null;

  const doneLinesHeader = (
    <div className="flex align-items-center" style={{ gap: "8px" }}>
      <i className="pi pi-check-circle" style={{ color: "#16a34a" }} />
      <span>
        {t(
          trackingBilling
            ? "entities.withholdings.withholdingsLines.billedGrid"
            : "entities.withholdings.withholdingsLines.confirmedGrid"
        )}
        {": "}
        {sinDatos ? "—" : `${doneLines.length} / ${visibleEntities.length}`}
      </span>
    </div>
  );

  const pendingLinesHeader = (
    <React.Fragment>
      <div className="flex align-items-center mb-2" style={{ gap: "8px" }}>
        <i className="pi pi-clock" style={{ color: "#2563eb" }} />
        <span>
          {t(
            trackingBilling
              ? "entities.withholdings.withholdingsLines.pendingBillingGrid"
              : "entities.withholdings.withholdingsLines.pendingGrid"
          )}
          {": "}
          {sinDatos ? "—" : pendingLines.length}
        </span>
      </div>
      {header}
    </React.Fragment>
  );

  const renderLinesTable = (rows, tableHeader, tableRef) => (
    <DataTable
      expandedRows={expandedRows}
      onRowToggle={(e) => setExpandedRows(e.data)}
      rowExpansionTemplate={rowExpansionTemplate}
      onSort={(e) => {
        setSortField(e.sortField);
        setSortOrder(e.sortOrder);
      }}
      sortField={sortField}
      sortOrder={sortOrder}
      ref={tableRef}
      value={rows}
      loading={linesLoading}
      first={first}
      rows={rows.length}
      totalRecords={rows.length}
      columnResizeMode="fit"
      showGridlines
      dataKey="id"
      paginatorTemplate="CurrentPageReport"
      currentPageReportTemplate={`${t(
        "DataTable.currentPageReportTemplate"
      )} ${t("WithholdingsLines")}`}
      globalFilter={globalFilter}
      header={tableHeader}
      emptyMessage={
        sinDatos
          ? t("entities.withholdings.withholdingsLines.loadFailedShort")
          : t("entities.withholdings.withholdingsLines.noLines")
      }
      className="flex flex-column w-full"
    >
        <Column expander style={{ width: "3rem" }} />
        {/* <Column
          field="installment_Number"
          header={t(
            "entities.withholdings.withholdingsLines.installment_Number"
          )}
          sortable
        ></Column> */}
        <Column
          field="beneficiary_DocumentNumber"
          bodyStyle={{ textAlign: "center" }}
          header={t(
            "entities.withholdings.withholdingsLines.beneficiary_DocumentNumber"
          )}
          sortable
        ></Column>
        <Column
          field="beneficiary_Name"
          header={t("entities.withholdings.withholdingsLines.beneficiary_Name")}
          sortable
        ></Column>
        <Column
          field="beneficiary_LastName"
          header={t(
            "entities.withholdings.withholdingsLines.beneficiary_LastName"
          )}
          sortable
        ></Column>
        <Column
          field="amountSent"
          bodyStyle={{ textAlign: "right" }}
          header={t("entities.withholdings.withholdingsLines.amountSent")}
          body={
            settings.currentWithholdingState === "WITHHOLDING_STATE_CREATED"
              ? withholdingLineAmount
              : (rowData) => {
                return helperFormats.formatDecimal(rowData.amountSent);
              }
          }
          sortable
        ></Column>
        {settings.currentWithholdingState != "WITHHOLDING_STATE_CREATED" && (
          <Column
            field="amountConfirmed"
            bodyStyle={{ textAlign: "right" }}
            header={t(
              "entities.withholdings.withholdingsLines.amountConfirmed"
            )}
            body={
              settings.currentWithholdingState === "WITHHOLDING_STATE_SENT"
                ? withholdingLineAmount
                : (rowData) => {
                  return helperFormats.formatDecimal(rowData.amountConfirmed);
                }
            }
            sortable
          ></Column>
        )}
        {settings.currentWithholdingState != "WITHHOLDING_STATE_CREATED" &&
          settings.currentWithholdingState != "WITHHOLDING_STATE_SENT" && (
            <Column
              field="amountConcilied"
              bodyStyle={{ textAlign: "right" }}
              header={t(
                "entities.withholdings.withholdingsLines.amountConcilied"
              )}
              body={
                settings.currentWithholdingState ===
                  "WITHHOLDING_STATE_CONFIRMED"
                  ? withholdingLineAmount
                  : (rowData) => {
                    return helperFormats.formatDecimal(
                      rowData.amountConcilied
                    );
                  }
              }
              sortable
            ></Column>
          )}
        {showConfirmationGrids && (
          <Column
            body={confirmationStatusBodyTemplate}
            header={t("entities.withholdings.withholdingsLines.status.header")}
          ></Column>
        )}
        <Column
          body={mismatchAlertBodyTemplate}
          bodyStyle={{ textAlign: "center" }}
          headerStyle={{ width: "3rem" }}
          style={{ width: "3rem" }}
        ></Column>
    </DataTable>
  );

  return (
    <div className={s.get("dataTableContainer")}>
      {sinDatos && (
        // El cartel va SÓLO cuando no hay ninguna línea para mostrar. Si las líneas están
        // cargadas se muestran en sus grillas, que es lo que sirve para saber en qué quedó cada
        // beneficiario; avisar del error ahí arriba sería taparlo con un mensaje.
        <div
          className="flex align-items-center justify-content-between w-full mb-3 p-3"
          style={{
            gap: "12px",
            border: "1px solid #fca5a5",
            borderRadius: "6px",
            background: "#fef2f2",
          }}
        >
          <div className="flex align-items-center" style={{ gap: "8px" }}>
            <i className="pi pi-exclamation-triangle" style={{ color: "#dc2626" }} />
            <span style={{ color: "#991b1b" }}>
              {t("entities.withholdings.withholdingsLines.loadFailed")}
            </span>
          </div>
          <Button
            label={t("entities.withholdings.withholdingsLines.loadRetry")}
            icon="pi pi-refresh"
            className="p-button-sm"
            onClick={fetchData}
            loading={linesLoading}
            type="button"
          />
        </div>
      )}
      {showConfirmationGrids ? (
        <React.Fragment>
          {renderLinesTable(pendingLines, pendingLinesHeader, dt)}

          <div className="mt-4 w-full">
            {renderLinesTable(doneLines, doneLinesHeader, billedDt)}
          </div>
        </React.Fragment>
      ) : (
        renderLinesTable(visibleEntities, header, dt)
      )}
      <React.Fragment>
        <Dialog
          visible={updateWithholdingLineAmountDialog}
          header={t("entities.withholdings.withholdingsLines.editAmount")}
          modal
          className="flex w-6"
          onHide={hideDialog}
        >
          <WithholdingsLinesUpdateAmount
            settings={{
              updateWithholdingLineId,
              updateWithholdingLineAmount,
              withholdingState: settings.currentWithholdingState,
              updateWithholdingLine,
              hideDialog,
              toast: settings.toast,
            }}
          />
        </Dialog>
        <Dialog
          visible={resolveBillingDialog}
          header={t(
            "entities.withholdings.withholdingsLines.resolveBilling.title"
          )}
          modal
          className="flex w-6"
          onHide={hideDialog}
        >
          <WithholdingsLinesResolveBilling
            settings={{
              resolveWithholdingLineId,
              resolveWithholdingLineBeneficiary,
              hideDialog,
              toast: settings.toast,
            }}
          />
        </Dialog>
      </React.Fragment>
    </div>
  );
};

export default WithholdingsLines;




